<script lang="ts">import { goto } from "$app/navigation";
import { isPromoExternalAbsoluteUrl, isRewardPromo, isRewardPromoClaimReady, openPromoExternalInNewTab } from "../utils";
import { FeatureSet, stateFeatureSet, stateUi } from "@infinitro/config";
import { isMobile, loggedIn, ModalDialog, pushModal, refreshVipState, stateOpenModal, stateVipState, toSiteUrl } from "@infinitro/global";
import { InfoCard } from "@infinitro/info-card";
import { get } from "svelte/store";
import PromoItemContentCustom from "./promo-item/promo-item-content-custom.svelte";
import PromoItemContent from "./promo-item/promo-item-content.svelte";
export let promo;
export let highlight;
export let isNew;
export let isDetail;
let active = false;
$: useLoggedInPromoCta = $loggedIn === true || ($stateUi.promotions?.promoCtasMatchLoggedInWhenLoggedOut ?? false);
const checkActive = (p, featureSet) => {
	const now = Date.now();
	const start = p?.startDate ? p.startDate.getTime() : null;
	const end = p?.endDate ? p.endDate.getTime() : null;
	active = !!(!start && !end || start && !end && now >= start || !start && end && now <= end || start && end && now >= start && now <= end || featureSet === FeatureSet.Preview);
};
const resolveUrl = () => {
	if (promo.conditionalUrl) {
		return $loggedIn === true ? promo.conditionalUrl.loggedInUrl : promo.conditionalUrl.loggedOutUrl;
	}
	return promo.url;
};
const getPromoLink = () => {
	if (promo.useUrlForDetail) {
		const resolvedUrl = resolveUrl();
		const toSiteIsLoggedIn = promo.conditionalUrl ? $loggedIn === true : useLoggedInPromoCta;
		return isPromoExternalAbsoluteUrl(resolvedUrl) ? resolvedUrl : toSiteUrl(resolvedUrl, toSiteIsLoggedIn);
	}
	if (promo.isFlushRace) {
		return toSiteUrl(promo.url ? promo.url : `/promotion-page/race/${promo.id}`, useLoggedInPromoCta);
	}
	if (promo.id === "referral") {
		return toSiteUrl(useLoggedInPromoCta ? `/referral-dashboard/` : `/referral/`, useLoggedInPromoCta);
	}
	return toSiteUrl(`/promotion-page/${promo.id}`, useLoggedInPromoCta);
};
const clickSignUp = () => {
	pushModal(ModalDialog.Auth, { tab: "signup" });
};
const clickCashier = () => {
	pushModal(ModalDialog.Cashier);
};
const getButtonLink = () => {
	if (promo.promotionUrl) {
		return isPromoExternalAbsoluteUrl(promo.promotionUrl) ? promo.promotionUrl : toSiteUrl(promo.promotionUrl, useLoggedInPromoCta);
	}
	const resolvedUrl = resolveUrl();
	const toSiteIsLoggedIn = promo.conditionalUrl ? $loggedIn === true : useLoggedInPromoCta;
	return isPromoExternalAbsoluteUrl(resolvedUrl) ? resolvedUrl : toSiteUrl(resolvedUrl, toSiteIsLoggedIn);
};
const openPromoLink = (url) => {
	if (isPromoExternalAbsoluteUrl(url)) {
		openPromoExternalInNewTab(url);
		return;
	}
	goto(url);
};
const getRewardFallbackUrl = () => toSiteUrl($loggedIn === true ? "/vip-dashboard/" : "/vip/", $loggedIn === true);
const openRewardsDropdown = () => {
	stateOpenModal.set(ModalDialog.RewardsDropdown);
};
const handleRewardPromoAction = async () => {
	if ($loggedIn !== true) {
		goto(getRewardFallbackUrl());
		return;
	}
	const vipStateSnapshot = get(stateVipState);
	if (Array.isArray(vipStateSnapshot?.rewards)) {
		if (isRewardPromoClaimReady(promo, vipStateSnapshot)) {
			openRewardsDropdown();
			return;
		}
		goto(getRewardFallbackUrl());
		return;
	}
	await refreshVipState();
	const refreshedVipState = get(stateVipState);
	if (isRewardPromoClaimReady(promo, refreshedVipState)) {
		openRewardsDropdown();
		return;
	}
	goto(getRewardFallbackUrl());
};
const runCardButtonAction = () => {
	if (isRewardPromo(promo)) {
		handleRewardPromoAction();
		return;
	}
	if (useLoggedInPromoCta) {
		if (promo.action) {
			if (promo.id === "deposit_bonus" || promo.id === "welcome-deposit-bonus") {
				clickCashier();
				return;
			}
			if (promo.id === "referral") {
				goto(toSiteUrl(`/referral-dashboard/`, useLoggedInPromoCta));
				return;
			}
			if (promo.customAction) {
				if (isPromoExternalAbsoluteUrl(promo.customAction)) {
					openPromoExternalInNewTab(promo.customAction);
				} else {
					goto(toSiteUrl(promo.customAction, useLoggedInPromoCta));
				}
				return;
			}
			if (promo.useUrlForDetail || promo.promotionUrl || promo.conditionalUrl) {
				openPromoLink(getButtonLink());
				return;
			}
			goto(toSiteUrl(`/casino`, useLoggedInPromoCta));
			return;
		}
		openPromoLink(getButtonLink());
		return;
	}
	if (promo.action) {
		clickSignUp();
		return;
	}
	openPromoLink(getButtonLink());
};
$: checkActive(promo, $stateFeatureSet);
$: isExpired = $stateUi?.promotions?.showExpiredPromotions && !!promo?.endDate && Date.now() > promo?.endDate.getTime();
$: useAlternative2 = $stateUi.promotions.promoBannerStyle === "alternative2";
</script>

<InfoCard
	noPadding={true}
	{highlight}
	borderRadius={isDetail && !$isMobile ? '36px' : '24px'}
	radialGradient2BeforeTopColor="var(--color-text-700)"
	customStylesCard="background: transparent !important;"
>
	<div
		class:aspect-ratio-box={!$isMobile}
		class:aspect-ratio-box-mobile={$isMobile}
		class:detail-aspect-ratio-box={isDetail}
	>
		{#if isDetail}
			{#if $stateUi.layout.usePromoItemContentCustom}
				<PromoItemContentCustom {promo} {active} {isNew} {isDetail} />
			{:else}
				<PromoItemContent {promo} {active} {isNew} {isDetail} />
			{/if}
		{:else}
			<!-- svelte-ignore missing-declaration -->
			<!-- svelte-ignore a11y-click-events-have-key-events -->
			<div
				role="button"
				tabindex="0"
				on:click={(e) => {
					if ((!active || isExpired) && !promo?.isFlushRace) return;
					e.stopPropagation();
					if (isRewardPromo(promo)) {
						runCardButtonAction();
						return;
					}
					if (!promo.bannerBig) {
						runCardButtonAction();
						return;
					}
					const url =
						promo.id === 'sports' ? toSiteUrl(`/sports/`, useLoggedInPromoCta) : getPromoLink();
					openPromoLink(url);
				}}
			>
				{#if $stateUi.layout.usePromoItemContentCustom}
					<PromoItemContentCustom {promo} {active} {isNew} {isDetail} />
				{:else}
					<PromoItemContent {promo} {active} {isNew} {isDetail} />
				{/if}
			</div>
		{/if}
	</div>
</InfoCard>

<style>
	.aspect-ratio-box {
		position: relative;
		width: 100%;
		padding-bottom: calc(304 / 387 * 100%);
	}

	.aspect-ratio-box-mobile {
		position: relative;
		width: 100%;
		padding-bottom: calc(304 / 361 * 100%);
	}

	.detail-aspect-ratio-box {
		position: relative;
		width: 100%;
		padding-bottom: 0;
	}
</style>
